/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { FontIcon } from '@fluentui/react/lib/Icon';
import { Stack } from '@fluentui/react/lib/Stack';
import React, { useRef } from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { DonutChart } from 'src/core/components/DonutChart';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { ChartAxisData } from 'src/core/types/chart';
import { numberToString, truncateValue } from 'src/core/utils/number';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { ModelEvaluationStore } from 'src/modules/modelDetails/stores/ModelEvaluationStore';
import { SchemaType, getSingleModelName, getStrictLabel } from 'src/modules/modelGuidance/modelGuidanceUtils/modelGuidanceUtils';
import styled from 'styled-components';

export type ModelDetailsCardProps = {
    pivotName?: SchemaType;
    fileTypeMessage?: string;
    numberOfTestingFiles?: number;
    numberOfTrainingFiles?: number;
    onLearnMoreLinkClick: (linkRef: React.RefObject<HTMLAnchorElement>) => void;
    modelEvaluationStore: ModelEvaluationStore;
    trainModelInfo: { lastTrainingDate: Date; lastTrainingDuration: number };
};

type PerformanceMetric = 'precision' | 'recall' | 'f1Score';

const messages = defineMessages({
    status: { id: 'modelGuidance.modelDetailsCard.status', defaultMessage: 'Status' },
    recall: { id: 'modelGuidance.modelDetailsCard.recall', defaultMessage: 'Recall:' },
    f1Score: { id: 'modelGuidance.modelDetailsCard.f1Score', defaultMessage: 'F1 score:' },
    manualType: { id: 'modelGuidance.modelDetailsCard.manualType', defaultMessage: 'Manual' },
    documents: { id: 'modelGuidance.modelDetailsCard.documents', defaultMessage: 'documents' },
    precision: { id: 'modelGuidance.modelDetailsCard.precision', defaultMessage: 'Precision:' },
    percentageType: { id: 'modelGuidance.modelDetailsCard.percentageType', defaultMessage: 'Percentage' },
    dataProcessing: { id: 'modelGuidance.modelDetailsCard.dataProcessing', defaultMessage: 'Data processing' },
    trainingFailed: { id: 'modelGuidance.modelDetailsCard.trainingFailed', defaultMessage: 'Training Failed' },
    totalTrainingTime: { id: 'modelGuidance.modelDetailsCard.totalTrainingTime', defaultMessage: 'Total training time:' },
    metricAriaLabel: { id: 'modelGuidance.modelDetailsCard.metricAriaLabel', defaultMessage: '{metric} value is {value}' },
    trainingInProgress: { id: 'modelGuidance.modelDetailsCard.trainingInProgress', defaultMessage: 'Training in progress' },
    finishedTrainingOn: { id: 'modelGuidance.modelDetailsCard.finishedTrainingOn', defaultMessage: 'Finished training on:' },
    trainedSuccessfully: { id: 'modelGuidance.modelDetailsCard.trainedSuccessfully', defaultMessage: 'Trained successfully' },
    learnMore: { id: 'modelGuidance.modelDetailsCard.learnMore', defaultMessage: 'Learn more about how to improve your model' },
    trainingTime: { id: 'modelGuidance.modelDetailsCard.trainingTime', defaultMessage: 'Training model will take a few hours.' },
    numberOfTrainingDocuments: {
        id: 'modelGuidance.modelDetailsCard.numberOfTrainingDocuments',
        defaultMessage: 'Number of training {fileType}:'
    },
    numberOfTestingDocuments: {
        id: 'modelGuidance.modelDetailsCard.numberOfTestingDocuments',
        defaultMessage: 'Number of testing {fileType}:'
    },
    timeString: {
        id: 'modelGuidance.modelDetailsCard.timeString',
        defaultMessage: '{ hours } hour(s), { minutes } minute(s), { seconds } second(s)'
    },
    f1ScoreCalloutDescription: {
        id: 'modelGuidance.modelDetailsCard.f1ScoreCalloutDescription',
        defaultMessage:
            'The {strictLabel} F1 score represents the overall predictive accuracy of your model as measured during performance testing. It’s a weighted average between the precision and recall scores, and it ranges from 0% to 100%.'
    },
    precisionCalloutDescription: {
        id: 'modelGuidance.modelDetailsCard.precisionCalloutDescription',
        defaultMessage:
            'The {strictLabel} precision score compares correct predictions of each {labelType} to all predictions of each {labelType}. It’s a ratio that incorporates false positives but not false negatives. It’s a good metric to look at when the cost of false positives is high, such as email spam detection. It ranges from 0% to 100%.'
    },
    recallCalloutDescription: {
        id: 'modelGuidance.modelDetailsCard.recallCalloutDescription',
        defaultMessage:
            'The {strictLabel} recall score compares correct predictions of each {labelType} to all actual instances of each {labelType}. It’s a ratio that incorporates false negatives but not false positives. It’s a good metric to look at when the cost of false negatives is high, such as fraud detection or cancer detection. It ranges from 0% to 100%.'
    },
    trainingDataSplittingType: {
        id: 'modelGuidance.modelDetailsCard.trainingDataSplittingType',
        defaultMessage: 'Training data splitting type:'
    }
});

const theme = getScopedTheme('ModelGuidanceModelDetailsCard');

const CardText = styled(LuisText)({ fontWeight: 600 });
const Card = styled(Stack)({
    borderWidth: '1px',
    borderStyle: 'solid',
    boxShadow: theme.boxShadow,
    padding: '12px 24px 24px 24px',
    borderColor: theme.borderColor
});

/**
 * Displays the details of model trining status and performance metrics
 * when the training is done.
 */
export const ModelDetailsCard = (props: ModelDetailsCardProps) => {
    const { localize, locale } = useLocalization();
    const { isXLargeOrGreater } = useResponsiveMode();
    const {
        pivotName,
        trainModelInfo,
        modelEvaluationStore,
        numberOfTestingFiles,
        numberOfTrainingFiles,
        fileTypeMessage = localize(messages.documents),
        onLearnMoreLinkClick
    } = props;

    const linkRef = useRef<HTMLAnchorElement | null>(null);

    const { serviceName } = getPortalFromUrl();

    const performanceMetrics: PerformanceMetric[] = ['f1Score', 'precision', 'recall'];

    const secondsToTimeString = (s: number) => {
        const seconds = s % 60;
        s = (s - seconds) / 60;
        const minutes = s % 60;
        const hours = (s - minutes) / 60;

        return localize(messages.timeString, { hours, minutes, seconds });
    };

    const renderPerformanceMetrics = (metric: PerformanceMetric) => {
        const xData: ChartAxisData<string> = {
            label: 'Metric',
            data: [metric, 'empty']
        };
        const yData: ChartAxisData<number> = {
            label: 'Value',
            data: [
                modelEvaluationStore ? modelEvaluationStore[metric] * 100 : 0,
                100 - (modelEvaluationStore ? modelEvaluationStore[metric] * 100 : 0)
            ]
        };

        const ariaLabel = localize(messages.metricAriaLabel, { metric, value: `${truncateValue(yData.data[0], 2)}%` });
        const colors = [metric === 'f1Score' ? theme.f1Score : metric === 'precision' ? theme.precision : theme.recall, theme.emptyMetric];

        return (
            <Stack role={'group'} tabIndex={0} key={metric} horizontalAlign="center" aria-label={ariaLabel}>
                <DonutChart
                    width={15}
                    size="medium"
                    xData={xData}
                    yData={yData}
                    colors={colors}
                    areTitlesInOrder={false}
                    donutSubtitle={localize(messages[metric])}
                    donutTitle={`${truncateValue(yData.data[0], 2)}%`}
                    titleFontWeight={theme.donutChartTitleFontWeight as string}
                    subtitleFontWeight={theme.donutChartSubtitleFontWeight as string}
                />
            </Stack>
        );
    };

    return (
        <Card tokens={{ childrenGap: 25 }}>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
                <CardText variant="large">{localize(messages.status)}:</CardText>

                <FontIcon iconName="CompletedSolid" data-automation-id="trainedSuccessfully" style={{ color: 'green' }} />
                <CardText variant="large">{localize(messages.trainedSuccessfully)}</CardText>
            </Stack>

            <Stack
                verticalAlign="center"
                tokens={{ childrenGap: 60 }}
                horizontal={isXLargeOrGreater}
                styles={{ root: { flex: 1 } }}
                horizontalAlign={isXLargeOrGreater ? 'space-evenly' : 'center'}>
                <Stack styles={{ root: { width: '20%' } }} horizontalAlign="center">
                    {renderPerformanceMetrics('f1Score')}
                </Stack>

                <Stack
                    verticalFill
                    tokens={{ childrenGap: 18 }}
                    horizontalAlign={isXLargeOrGreater ? 'start' : 'center'}
                    styles={{ root: { width: isXLargeOrGreater ? '40%' : '60%' } }}>
                    <Stack styles={{ root: { width: '50%' } }}>
                        <Stack horizontal horizontalAlign="space-between">
                            <CardText>
                                {localize(messages.f1Score)}
                                <CalloutButton
                                    content={localize(messages.f1ScoreCalloutDescription, {
                                        labelType: localize(getSingleModelName(pivotName, serviceName)),
                                        strictLabel: localize(getStrictLabel(pivotName, serviceName))
                                    })}
                                />
                            </CardText>
                            <CardText>{`${truncateValue(modelEvaluationStore?.f1Score * 100, 2)}%`}</CardText>
                        </Stack>
                        <Stack horizontal horizontalAlign="space-between">
                            <CardText>
                                {localize(messages.precision)}
                                <CalloutButton
                                    content={localize(messages.precisionCalloutDescription, {
                                        labelType: localize(getSingleModelName(pivotName, serviceName)),
                                        strictLabel: localize(getStrictLabel(pivotName, serviceName))
                                    })}
                                />
                            </CardText>
                            <CardText>{`${truncateValue(modelEvaluationStore?.precision * 100, 2)}%`}</CardText>
                        </Stack>
                        <Stack horizontal horizontalAlign="space-between">
                            <CardText>
                                {localize(messages.recall)}
                                <CalloutButton
                                    content={localize(messages.recallCalloutDescription, {
                                        labelType: localize(getSingleModelName(pivotName)),
                                        strictLabel: localize(getStrictLabel(pivotName, serviceName))
                                    })}
                                />
                            </CardText>
                            <CardText>{`${truncateValue(modelEvaluationStore?.recall * 100, 2)}%`}</CardText>
                        </Stack>
                    </Stack>
                    <Stack>
                        <Stack horizontal tokens={{ childrenGap: 15 }}>
                            <CardText>{localize(messages.finishedTrainingOn)}</CardText>
                            <LuisText>{trainModelInfo?.lastTrainingDate.toLocaleString(locale)}</LuisText>
                        </Stack>

                        <Stack horizontal tokens={{ childrenGap: 15 }}>
                            <CardText>{localize(messages.totalTrainingTime)}</CardText>
                            <LuisText>{secondsToTimeString(trainModelInfo.lastTrainingDuration)}</LuisText>
                        </Stack>
                    </Stack>
                    <Stack styles={{ root: { width: '55%' } }}>
                        <Stack horizontal horizontalAlign="space-between">
                            <CardText>{localize(messages.trainingDataSplittingType)}</CardText>
                            <LuisText>
                                {localize(
                                    modelEvaluationStore?.trainingSplitType === 'percentage' ? messages.percentageType : messages.manualType
                                )}
                            </LuisText>
                        </Stack>

                        <Stack horizontal horizontalAlign="space-between">
                            <CardText>{localize(messages.numberOfTrainingDocuments, { fileType: fileTypeMessage })}</CardText>
                            <LuisText>
                                {numberToString(numberOfTrainingFiles)}
                                {numberOfTrainingFiles && (
                                    <LuisText>
                                        {` (${truncateValue(
                                            (100 * numberOfTrainingFiles) / (numberOfTrainingFiles + numberOfTestingFiles),
                                            2
                                        )}%)`}
                                    </LuisText>
                                )}
                            </LuisText>
                        </Stack>

                        <Stack horizontal horizontalAlign="space-between">
                            <CardText>{localize(messages.numberOfTestingDocuments, { fileType: fileTypeMessage })}</CardText>
                            <LuisText>
                                {numberToString(numberOfTestingFiles)}
                                {numberOfTrainingFiles && (
                                    <LuisText>
                                        {` (${truncateValue(
                                            (100 * numberOfTestingFiles) / (numberOfTrainingFiles + numberOfTestingFiles),
                                            2
                                        )}%)`}
                                    </LuisText>
                                )}
                            </LuisText>
                        </Stack>
                    </Stack>

                    <ExternalLink
                        isUnderlined={false}
                        innerRef={linkRef}
                        onClick={() => onLearnMoreLinkClick(linkRef)}
                        styles={{ root: { marginTop: '26px !important' } }}>
                        {localize(messages.learnMore)}
                    </ExternalLink>
                </Stack>

                <Stack.Item align="center" styles={{ root: { flex: 1, width: '40%' } }}>
                    <Stack horizontal={isXLargeOrGreater} horizontalAlign="space-evenly" tokens={isXLargeOrGreater && { childrenGap: 20 }}>
                        {performanceMetrics.map(metric => metric !== 'f1Score' && renderPerformanceMetrics(metric))}
                    </Stack>
                </Stack.Item>
            </Stack>
        </Card>
    );
};
